import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Modal, Badge, Spinner, Alert, Table, Tabs, Tab, Form } from 'react-bootstrap';
import '../styles/reservationListTabs.css';
import {
  Reservation,
  ReservationRules,
  getInstrumentReservations,
  approveReservation,
  endUsage,
  cancelReservation,
  submitEndUseRecord
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import dayjs from 'dayjs';
import EndUseRecordModal from './EndUseRecordModal';
import {
  getEndUseRules,
  getInstrumentFromReservation,
  reservationHasEndUseRecord,
  type EndUsePayload
} from '../utils/endUseRecord';

interface ReservationListProps {
  instrumentId: string;
  instrumentName: string;
  instrumentResponsiblePerson?: string;
  instrumentReservationRules?: ReservationRules;
  onClose: () => void;
}

const parseResponsiblePersons = (value?: string): string[] => {
  if (!value) return [];
  return value.split(/[、,，;\s]+/).map(item => item.trim()).filter(Boolean);
};

const ReservationList: React.FC<ReservationListProps> = ({
  instrumentId,
  instrumentName,
  instrumentResponsiblePerson,
  instrumentReservationRules,
  onClose
}) => {
  const { user } = useAuth();
  const responsiblePersons = parseResponsiblePersons(instrumentResponsiblePerson);
  const isCurrentUserResponsible = Boolean(
    user &&
    responsiblePersons.length > 0 &&
    (
      responsiblePersons.includes(user.name) ||
      responsiblePersons.includes(user.username)
    )
  );

  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('using');
  const [showEndUsageModal, setShowEndUsageModal] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
  const [usageNotes, setUsageNotes] = useState('');
  const [showEndUseModal, setShowEndUseModal] = useState(false);
  const [endUseModalMode, setEndUseModalMode] = useState<'submit' | 'view'>('view');

  const endUseRules = getEndUseRules({ reservationRules: instrumentReservationRules });

  // 获取预约数据
  const fetchReservations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getInstrumentReservations(instrumentId);
      if (response.success) {
        setReservations(response.data || []);
      } else {
        setError(response.error || '获取预约数据失败');
      }
    } catch (error) {
      setError('获取预约数据失败');
      console.error('获取预约数据失败:', error);
    } finally {
      setLoading(false);
    }
  }, [instrumentId]);

  useEffect(() => {
    fetchReservations();
    
    // 每分钟自动刷新一次预约数据，以获取自动状态更新
    const interval = setInterval(fetchReservations, 60 * 1000);
    
    return () => clearInterval(interval);
  }, [fetchReservations]);

  // 分类预约数据
  const categorizeReservations = () => {
    const now = dayjs();
    const categorized = {
      using: [] as Reservation[],      // 使用中
      queued: [] as Reservation[],    // 排队中（已批准但未到时间）
      pending: [] as Reservation[],    // 待审批
      history: [] as Reservation[]     // 历史申请
    };

    reservations.forEach(reservation => {
      const startTime = dayjs(reservation.startTime);
      const endTime = dayjs(reservation.endTime);
      
      if (reservation.status === '使用中') {
        categorized.using.push(reservation);
      } else if (reservation.status === '待审核') {
        categorized.pending.push(reservation);
      } else if (reservation.status === '已批准') {
        // 已批准的预约根据时间分类
        if (startTime.isAfter(now)) {
          // 未到开始时间
          categorized.queued.push(reservation);
        } else if (endTime.isBefore(now)) {
          // 已过结束时间，应该被自动更新为已完成
          categorized.history.push(reservation);
        } else {
          // 在预约时间内，应该被自动更新为使用中
          categorized.using.push(reservation);
        }
      } else if (reservation.status === '已完成' || reservation.status === '已取消' || reservation.status === '已拒绝') {
        categorized.history.push(reservation);
      }
    });

    return categorized;
  };

  // 处理预约操作
  const handleReservationAction = async (reservationId: string, action: 'approve' | 'reject' | 'cancel', comment?: string) => {
    try {
      if (action === 'approve') {
        const approveRes = await approveReservation(reservationId, '已批准', comment || '已批准');
        if (approveRes.success) {
          await fetchReservations();
          return;
        }
        setError(approveRes.error || '审批失败');
        return;
      } else if (action === 'reject') {
        const rejectRes = await approveReservation(reservationId, '已拒绝', comment || '已拒绝');
        if (rejectRes.success) {
          await fetchReservations();
          return;
        }
        setError(rejectRes.error || '审批失败');
        return;
      } else if (action === 'cancel') {
        // 取消改为调用专用接口，校验开始时间和权限在后端
        const cancelRes = await cancelReservation(reservationId);
        if (cancelRes.success) {
          await fetchReservations();
          return;
        }
        setError(cancelRes.error || '取消失败');
        return;
      }

      await fetchReservations();
    } catch (error) {
      setError('操作失败');
      console.error('操作失败:', error);
    }
  };

  // 结束使用仪器
  const handleEndUsage = async () => {
    if (!selectedReservation) return;
    
    try {
      const response = await endUsage(selectedReservation._id!, { usageNotes });
      if (response.success) {
        await fetchReservations(); // 刷新数据
        setShowEndUsageModal(false);
        setSelectedReservation(null);
        setUsageNotes('');
      } else {
        setError(response.error || '结束使用失败');
      }
    } catch (error) {
      setError('结束使用失败');
      console.error('结束使用失败:', error);
    }
  };

  // 打开结束使用模态框
  const openEndUsageModal = (reservation: Reservation) => {
    setSelectedReservation(reservation);
    const ins = getInstrumentFromReservation(reservation);
    const rules = getEndUseRules(ins || { reservationRules: instrumentReservationRules });
    if (rules.enabled) {
      setEndUseModalMode('submit');
      setShowEndUseModal(true);
      return;
    }
    setUsageNotes('');
    setShowEndUsageModal(true);
  };

  const openEndUseView = (reservation: Reservation) => {
    setSelectedReservation(reservation);
    setEndUseModalMode('view');
    setShowEndUseModal(true);
  };

  const handleEndUseSubmit = async (payload: EndUsePayload) => {
    if (!selectedReservation?._id) return;
    if (selectedReservation.status === '使用中') {
      const response = await endUsage(selectedReservation._id, {
        usageNotes: payload.notes,
        endUseRecord: payload
      });
      if (!response.success) throw new Error(response.error || '结束使用失败');
    } else {
      const response = await submitEndUseRecord(selectedReservation._id, payload);
      if (!response.success) throw new Error(response.error || '提交失败');
    }
    await fetchReservations();
  };

  // 获取状态标签颜色
  const getStatusVariant = (status: string) => {
    switch (status) {
      case '使用中': return 'success';
      case '已批准': return 'primary';
      case '待审核': return 'warning';
      case '已拒绝': return 'danger';
      case '已完成': return 'secondary';
      case '已取消': return 'dark';
      default: return 'secondary';
    }
  };

  // 获取状态文本
  const getStatusText = (status: string) => {
    switch (status) {
      case '使用中': return '使用中';
      case '已批准': return '已批准';
      case '待审核': return '待审核';
      case '已拒绝': return '已拒绝';
      case '已完成': return '已完成';
      case '已取消': return '已取消';
      default: return status;
    }
  };

  // 渲染预约表格
  const renderReservationTable = (
    reservationList: Reservation[],
    showActions: boolean = false,
    showEndUseColumn: boolean = false
  ) => {
    if (reservationList.length === 0) {
      return (
        <div className="text-center py-4 text-muted">
          暂无预约记录
        </div>
      );
    }

    const shouldShowEndUseColumn =
      showEndUseColumn &&
      reservationList.some((reservation) => {
        const rules = getEndUseRules(
          getInstrumentFromReservation(reservation) || { reservationRules: instrumentReservationRules }
        );
        return rules.enabled || reservationHasEndUseRecord(reservation);
      });

    return (
      <Table responsive striped hover>
        <thead>
          <tr>
            <th>预约人</th>
            <th>预约时间</th>
            <th>使用时长</th>
            <th>预约目的</th>
            <th>状态</th>
            {showActions && <th>操作</th>}
            {shouldShowEndUseColumn && <th>结束使用记录</th>}
            {user && <th>使用控制</th>}
          </tr>
        </thead>
        <tbody>
          {reservationList.map((reservation) => {
            const rowEndUseRules = getEndUseRules(
              getInstrumentFromReservation(reservation) || { reservationRules: instrumentReservationRules }
            );
            return (
              <tr key={reservation._id}>
              <td>
                <div>
                  <strong>{typeof reservation.userId === 'object' ? reservation.userId.name : '未知用户'}</strong>
                  <br />
                  <small className="text-muted">
                    {typeof reservation.userId === 'object' ? reservation.userId.email : ''}
                  </small>
                </div>
              </td>
              <td>
                <div>
                  <div>{dayjs(reservation.startTime).format('YYYY-MM-DD HH:mm')}</div>
                  <div className="text-muted small">
                    至 {dayjs(reservation.endTime).format('HH:mm')}
                  </div>
                </div>
              </td>
              <td>
                {dayjs(reservation.endTime).diff(dayjs(reservation.startTime), 'minute')} 分钟
              </td>
              <td>
                {reservation.purpose || '无'}
              </td>
              <td>
                <Badge bg={getStatusVariant(reservation.status)}>
                  {getStatusText(reservation.status)}
                </Badge>
              </td>
              {shouldShowEndUseColumn && (
                <td>
                  {reservation.status === '已完成' && reservationHasEndUseRecord(reservation) ? (
                    <Button size="sm" variant="link" className="p-0" onClick={() => openEndUseView(reservation)}>
                      查看记录
                    </Button>
                  ) : reservation.status === '已完成' && rowEndUseRules.enabled ? (
                    <span className="text-muted small">未提交</span>
                  ) : (
                    '—'
                  )}
                </td>
              )}
              {showActions && (
                <td>
                  {reservation.status === '待审核' && (
                    <div className="btn-group" role="group">
                      <Button
                        size="sm"
                        variant="success"
                        onClick={() => handleReservationAction(reservation._id!, 'approve')}
                      >
                        批准
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => handleReservationAction(reservation._id!, 'reject')}
                      >
                        拒绝
                      </Button>
                    </div>
                  )}
                </td>
              )}
              
              {/* 使用控制：预约人可取消（时段未结束前）或结束使用 */}
              {user && typeof reservation.userId === 'object' && (
                (reservation.userId as any)._id === user.id || (reservation.userId as any).username === user.username
              ) && (
                <td>
                  {(reservation.status === '已批准' || reservation.status === '待审核') &&
                    new Date(reservation.endTime) > new Date() && (
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => handleReservationAction(reservation._id!, 'cancel')}
                    >
                      取消预约
                    </Button>
                  )}
                  {reservation.status === '使用中' && (
                    <Button
                      size="sm"
                      variant="warning"
                      onClick={() => openEndUsageModal(reservation)}
                    >
                      结束使用
                    </Button>
                  )}
                  {reservation.status === '已完成' &&
                    endUseRules.enabled &&
                    !reservationHasEndUseRecord(reservation) &&
                    (reservation.userId as { _id?: string })?._id === user?.id && (
                      <Button
                        size="sm"
                        variant="outline-primary"
                        className="ms-1"
                        onClick={() => {
                          setSelectedReservation(reservation);
                          setEndUseModalMode('submit');
                          setShowEndUseModal(true);
                        }}
                      >
                        提交结束记录
                      </Button>
                    )}
                </td>
              )}
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  };

  const categorized = categorizeReservations();

  if (loading) {
    return (
      <Modal show onHide={onClose} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>预约列表 - {instrumentName}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <div className="mt-3">加载中...</div>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <Modal show onHide={onClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>预约列表 - {instrumentName}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k || 'using')}
          className="lims-reservation-tabs mb-3"
        >
          <Tab eventKey="using" title={`使用中 (${categorized.using.length})`}>
            <Card>
              <Card.Header>
                <h6 className="mb-0">当前使用中的预约</h6>
                <small className="text-muted">含状态为「使用中」或时段内仍为「已批准」的预约（系统将自动开始）</small>
              </Card.Header>
              <Card.Body>
                {renderReservationTable(categorized.using)}
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="queued" title={`排队中 (${categorized.queued.length})`}>
            <Card>
              <Card.Header>
                <h6 className="mb-0">已批准但未到时间的预约</h6>
                <small className="text-muted">到达预约时间后系统将自动进入使用中</small>
              </Card.Header>
              <Card.Body>
                {renderReservationTable(categorized.queued)}
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="pending" title={`待审批 (${categorized.pending.length})`}>
            <Card>
              <Card.Header>
                <h6 className="mb-0">等待审批的预约</h6>
                <small className="text-muted">预约人可在「使用控制」列取消；取消后时段立即释放，并通知设备负责人</small>
              </Card.Header>
              <Card.Body>
                {renderReservationTable(categorized.pending, isCurrentUserResponsible)}
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="history" title={`历史申请 (${categorized.history.length})`}>
            <Card>
              <Card.Header>
                <h6 className="mb-0">历史预约记录</h6>
              </Card.Header>
              <Card.Body>
                {renderReservationTable(categorized.history, false, true)}
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          关闭
        </Button>
        <Button variant="primary" onClick={fetchReservations}>
          刷新
        </Button>
      </Modal.Footer>

      {/* 结束使用模态框 */}
      <Modal show={showEndUsageModal} onHide={() => setShowEndUsageModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>结束使用仪器</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedReservation && (
            <div>
              <div className="mb-3">
                <strong>仪器：</strong>{instrumentName}
              </div>
              <div className="mb-3">
                <strong>预约人：</strong>{typeof selectedReservation.userId === 'object' ? selectedReservation.userId.name : '未知用户'}
              </div>
              <div className="mb-3">
                <strong>预约时间：</strong>
                {dayjs(selectedReservation.startTime).format('YYYY-MM-DD HH:mm')} - {dayjs(selectedReservation.endTime).format('HH:mm')}
              </div>
              <div className="mb-3">
                <strong>实际开始时间：</strong>
                {selectedReservation.actualStartTime ? dayjs(selectedReservation.actualStartTime).format('YYYY-MM-DD HH:mm') : '未记录'}
              </div>
              
              <Form.Group className="mb-3">
                <Form.Label>使用备注</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={usageNotes}
                  onChange={(e) => setUsageNotes(e.target.value)}
                  placeholder="请输入使用过程中的备注信息..."
                />
              </Form.Group>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEndUsageModal(false)}>
            取消
          </Button>
          <Button variant="warning" onClick={handleEndUsage}>
            确认结束使用
          </Button>
        </Modal.Footer>
      </Modal>

      <EndUseRecordModal
        show={showEndUseModal}
        mode={endUseModalMode}
        reservation={selectedReservation}
        photoGroups={endUseRules.photoGroups}
        onHide={() => {
          setShowEndUseModal(false);
          setSelectedReservation(null);
        }}
        onSubmit={handleEndUseSubmit}
      />
    </Modal>
  );
};

export default ReservationList;
